import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../auth/useAuth";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import {
  AccountSuspendedError,
  createAccountServiceClient,
  type AccountProfile,
  type AccountProfilePatch,
  type HealthProfileExportSection,
} from "./client";

export type ProfileStatus = "idle" | "loading" | "ready" | "saving" | "error";

let pendingProfileKey: string | null = null;
let pendingProfileRequest: Promise<AccountProfile> | null = null;

export function useAccountProfile() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const apiBaseUrl = config?.accountService?.apiBaseUrl;
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [status, setStatus] = useState<ProfileStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // True once a normal profile read reports the account is suspended pending deletion. The app
  // renders the dedicated suspended-account experience instead of the workspace when this is set.
  const [suspended, setSuspended] = useState(false);

  const client = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }

    return createAccountServiceClient(apiBaseUrl, () => auth.user?.access_token);
  }, [apiBaseUrl, auth.user]);
  const profileCacheKey = useMemo(
    () => {
      if (!apiBaseUrl || !auth.user?.access_token) {
        return null;
      }

      const claims = auth.user.profile as Record<string, unknown> | undefined;
      const userKey = ["sub", "email", "preferred_username", "cognito:username", "username"]
        .map((key) => claims?.[key])
        .find((value): value is string => typeof value === "string" && value.trim().length > 0);

      return `${apiBaseUrl}:${userKey?.trim() ?? auth.user.access_token}`;
    },
    [apiBaseUrl, auth.user?.access_token, auth.user?.profile],
  );

  const loadProfile = useCallback(
    async (signal?: AbortSignal) => {
      if (!client) {
        setError("Profile management is not available yet.");
        setStatus("error");
        return;
      }

      setStatus("loading");
      setError(null);
      setSaved(false);
      setSuspended(false);

      try {
        const nextProfile =
          profileCacheKey && pendingProfileKey === profileCacheKey && pendingProfileRequest
            ? await pendingProfileRequest
            : await (() => {
                const request = client.getProfile();
                if (profileCacheKey) {
                  pendingProfileKey = profileCacheKey;
                  pendingProfileRequest = request;
                  const clearPendingRequest = () => {
                    if (pendingProfileRequest === request) {
                      pendingProfileKey = null;
                      pendingProfileRequest = null;
                    }
                  };
                  void request.then(clearPendingRequest, clearPendingRequest);
                }
                return request;
              })();

        if (signal?.aborted) {
          return;
        }

        setProfile(nextProfile);
        setStatus("ready");
      } catch (nextError) {
        if (nextError instanceof DOMException && nextError.name === "AbortError") {
          return;
        }

        if (nextError instanceof AccountSuspendedError) {
          setSuspended(true);
          setStatus("error");
          return;
        }

        setError(nextError instanceof Error ? nextError.message : "We could not load your profile.");
        setStatus("error");
      }
    },
    [client, profileCacheKey],
  );

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return undefined;
    }

    const controller = new AbortController();
    void loadProfile(controller.signal);

    return () => controller.abort();
  }, [auth.isAuthenticated, loadProfile]);

  useEffect(() => {
    if (!saved) {
      return undefined;
    }

    const timeout = window.setTimeout(() => setSaved(false), 3000);
    return () => window.clearTimeout(timeout);
  }, [saved]);

  const saveProfile = useCallback(
    async (patch: AccountProfilePatch) => {
      if (!client) {
        setError("Profile management is not available yet.");
        setStatus("error");
        return null;
      }

      setStatus("saving");
      setError(null);
      setSaved(false);

      try {
        const nextProfile = await client.updateProfile(patch);
        setProfile(nextProfile);
        setStatus("ready");
        setSaved(true);
        return nextProfile;
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "We could not save your profile.");
        setStatus("ready");
        return null;
      }
    },
    [client],
  );

  const exportHealthProfile = useCallback(
    async (sections: HealthProfileExportSection[]) => {
      if (!client) {
        setError("Profile export is not available yet.");
        setStatus("error");
        return null;
      }

      setStatus("saving");
      setError(null);

      try {
        const result = await client.exportHealthProfile(sections);
        setStatus("ready");
        return result;
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "We could not generate your health profile PDF.");
        setStatus("ready");
        return null;
      }
    },
    [client],
  );

  return {
    error,
    exportHealthProfile,
    loadProfile,
    profile,
    saveProfile,
    saved,
    status,
    suspended,
  };
}
